/**
 * Tenant Portal Routes
 *
 * CRUD operations use dialog-based forms
 * Pages are lazy-loaded for code splitting
 */

import { lazy } from 'react'
import type { RouteObject } from 'react-router-dom'
import { AppLayout } from '../components/layout'

const LandingPage = lazy(() => import('../pages/Landing'))
const LoginPage = lazy(() => import('../pages/Login'))
const DashboardPage = lazy(() => import('../pages/Dashboard'))
const SiteListPage = lazy(() => import('../pages/Sites/SiteList'))
const SiteDetailPage = lazy(() => import('../pages/Sites/SiteDetail'))
const MillListPage = lazy(() => import('../pages/Mills/MillList'))
const MillDetailPage = lazy(() => import('../pages/Mills/MillDetail'))
const LifterBarListPage = lazy(() => import('../pages/LifterBars/LifterBarList'))
const LifterBarDetailPage = lazy(() => import('../pages/LifterBars/LifterBarDetail'))
const CoordinatorListPage = lazy(() => import('../pages/Coordinators/CoordinatorList'))
const CoordinatorDetailPage = lazy(() => import('../pages/Coordinators/CoordinatorDetail'))
const SensorListPage = lazy(() => import('../pages/Sensors/SensorList'))
const SensorDetailPage = lazy(() => import('../pages/Sensors/SensorDetail'))
const UserListPage = lazy(() => import('../pages/Users/UserList'))
const RolesPage = lazy(() => import('../pages/Roles'))
const RawReadingsPage = lazy(() => import('../pages/RawReadings'))
const ProfilePage = lazy(() => import('../pages/Profile'))
const SettingsPage = lazy(() => import('../pages/Settings'))

// Navigation items for the sidebar
const navItems = [
  {
    title: 'nav.dashboard',
    href: '/',
    icon: 'LayoutDashboard' as const,
  },
  {
    title: 'nav.sites',
    href: '/sites',
    icon: 'MapPin' as const,
  },
  {
    title: 'nav.mills',
    href: '/mills',
    icon: 'Settings' as const,
  },
  {
    title: 'nav.lifterBars',
    href: '/lifter-bars',
    icon: 'Settings' as const,
  },
  {
    title: 'nav.coordinators',
    href: '/coordinators',
    icon: 'Wifi' as const,
  },
  {
    title: 'nav.sensors',
    href: '/sensors',
    icon: 'Radio' as const,
  },
  {
    title: 'nav.rawReadings',
    href: '/raw-readings',
    icon: 'Database' as const,
    adminOnly: true,
  },
  {
    title: 'nav.users',
    href: '/users',
    icon: 'Users' as const,
    adminOnly: true,
  },
  {
    title: 'nav.roles',
    href: '/roles',
    icon: 'Shield' as const,
    adminOnly: true,
  },
  {
    title: 'nav.settings',
    href: '/settings',
    icon: 'Settings' as const,
    adminOnly: true,
  },
]

export const routes: RouteObject[] = [
  {
    path: '/landing',
    element: <LandingPage />,
  },
  {
    path: '/login',
    element: <LoginPage />,
  },
  {
    path: '/',
    element: <AppLayout navItems={navItems} />,
    children: [
      {
        index: true,
        element: <DashboardPage />,
      },
      // Sites
      {
        path: 'sites',
        element: <SiteListPage />,
      },
      {
        path: 'sites/:id',
        element: <SiteDetailPage />,
      },
      // Mills
      {
        path: 'mills',
        element: <MillListPage />,
      },
      {
        path: 'mills/:id',
        element: <MillDetailPage />,
      },
      // Lifter Bars
      {
        path: 'lifter-bars',
        element: <LifterBarListPage />,
      },
      {
        path: 'lifter-bars/:id',
        element: <LifterBarDetailPage />,
      },
      // Coordinators
      {
        path: 'coordinators',
        element: <CoordinatorListPage />,
      },
      {
        path: 'coordinators/:coId',
        element: <CoordinatorDetailPage />,
      },
      // Sensors
      {
        path: 'sensors',
        element: <SensorListPage />,
      },
      {
        path: 'sensors/:recAdr',
        element: <SensorDetailPage />,
      },
      // Raw Readings (admin only - guarded by component)
      {
        path: 'raw-readings',
        element: <RawReadingsPage />,
      },
      // Users (admin only - guarded by component)
      {
        path: 'users',
        element: <UserListPage />,
      },
      // Roles (admin only - guarded by component)
      {
        path: 'roles',
        element: <RolesPage />,
      },
      // Settings (admin only - guarded by component)
      {
        path: 'settings',
        element: <SettingsPage />,
      },
      // Profile
      {
        path: 'profile',
        element: <ProfilePage />,
      },
    ],
  },
]
